import type { VsmProcess } from './flowModel';

export type StreamMappingState = 'not-started' | 'in-capture' | 'baseline-captured';
export type StreamMappingAction = 'start-mapping' | 'resume-mapping' | 'open-vsm';

export interface StreamRecordLike {
  id: string;
  name: string;
  smes?: unknown[];
  mappingSessionId?: string;
  vsmBaseline?: unknown;
}

export interface StreamMappingStatus {
  state: StreamMappingState;
  label: string;
  primaryAction: StreamMappingAction;
}

export interface StreamVsmMetrics {
  leadTimeSeconds: number;
  valueAddedSeconds: number;
  waitWasteSeconds: number;
  pcePercent: number;
  rolledCompleteAccuratePercent: number;
  incomplete: boolean;
  incompleteStepIds: number[];
}

export function deriveStreamMappingStatus(stream: StreamRecordLike): StreamMappingStatus {
  if (stream.vsmBaseline) {
    return {
      state: 'baseline-captured',
      label: 'Baseline captured',
      primaryAction: 'open-vsm',
    };
  }
  if (stream.mappingSessionId) {
    return {
      state: 'in-capture',
      label: 'In capture',
      primaryAction: 'resume-mapping',
    };
  }
  return {
    state: 'not-started',
    label: 'Not started',
    primaryAction: 'start-mapping',
  };
}

export function deriveStreamVsmMetrics(process: VsmProcess): StreamVsmMetrics {
  let valueAddedSeconds = 0;
  let waitWasteSeconds = 0;
  let rolledCa = 1;
  const incompleteStepIds: number[] = [];

  for (const step of process.steps ?? []) {
    const ct = parseStrictDurationSeconds(step.ct);
    const wt = parseStrictDurationSeconds(step.wt);
    const ca = Number(step.ca);
    const incomplete =
      !ct.valid || !wt.valid || !Number.isFinite(ca) || ca < 0 || ca > 100;

    if (incomplete) incompleteStepIds.push(step.statementId);
    if (ct.valid) valueAddedSeconds += ct.seconds;
    if (wt.valid) waitWasteSeconds += wt.seconds;
    if (Number.isFinite(ca)) rolledCa *= Math.max(0, Math.min(100, ca)) / 100;
  }

  const leadTimeSeconds = valueAddedSeconds + waitWasteSeconds;
  return {
    leadTimeSeconds,
    valueAddedSeconds,
    waitWasteSeconds,
    pcePercent: leadTimeSeconds > 0 ? round1((valueAddedSeconds / leadTimeSeconds) * 100) : 0,
    rolledCompleteAccuratePercent: round1(rolledCa * 100),
    incomplete: incompleteStepIds.length > 0,
    incompleteStepIds,
  };
}

function parseStrictDurationSeconds(raw: string | number | null | undefined): {
  valid: boolean;
  seconds: number;
} {
  if (raw == null) return { valid: false, seconds: 0 };
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? { valid: true, seconds: raw } : { valid: false, seconds: 0 };
  }
  const trimmed = String(raw).trim().toLowerCase();
  if (!trimmed) return { valid: false, seconds: 0 };
  if (trimmed === '0') return { valid: true, seconds: 0 };
  const match = trimmed.match(/^(\d+(?:\.\d+)?)\s*(s|m|h|d)?$/);
  if (!match) return { valid: false, seconds: 0 };
  const value = parseFloat(match[1]);
  if (!Number.isFinite(value)) return { valid: false, seconds: 0 };
  switch (match[2]) {
    case 'd':
      return { valid: true, seconds: value * 86400 };
    case 'h':
      return { valid: true, seconds: value * 3600 };
    case 'm':
      return { valid: true, seconds: value * 60 };
    case 's':
    default:
      return { valid: true, seconds: value };
  }
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}
